import { App, message as antdMessage } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import React, { useEffect } from 'react';
import i18n, { formatDate } from '../i18n';
import { REQUEST_HALT_ERROR_CODE, isRequestHaltedError } from './requestHalt';

let messageApi: MessageInstance | null = null;

export const setMessageInstance = (instance: MessageInstance) => {
  messageApi = instance;
};

/**
 * User-facing text of an error, in the current UI language:
 *   1. translation of `errorCode` from the shared `errors` namespace (params from `meta`);
 *   2. the server `message` (the server already localized it by Accept-Language);
 *   3. the message of a client-side `Error` / a plain string (local validation);
 *   4. `common:errors.generic`.
 */
export const getErrorMessage = (error: any): string => {
  if (typeof error === 'string') {
    return error;
  }

  const errorCode = getErrorCode(error);
  if (errorCode === REQUEST_HALT_ERROR_CODE || error?.code === REQUEST_HALT_ERROR_CODE) {
    return i18n.t('common:errors.requestsHalted');
  }
  const openDayText = getOpenDayErrorMessage(error);
  if (openDayText) {
    return openDayText;
  }
  if (errorCode && i18n.exists(`errors:${errorCode}`)) {
    const params: Record<string, unknown> = { ...(getErrorMeta(error) || {}) };
    return String(i18n.t(`errors:${errorCode}`, params));
  }

  // RTK Query error format: error.data.message (FetchBaseQueryError)
  if (typeof error?.data?.message === 'string' && error.data.message) {
    return error.data.message;
  }

  // Axios error format: error.response.data.message
  if (typeof error?.response?.data?.message === 'string' && error.response.data.message) {
    return error.response.data.message;
  }

  // No response at all: offline, DNS, CORS, server down.
  if (error?.status === 'FETCH_ERROR' || (error?.isAxiosError && !error.response)) {
    return i18n.t('common:errors.network');
  }

  // A client-side Error / RTK SerializedError from our own code
  // (axios' own messages are untranslated transport prose, so they are skipped).
  if (typeof error?.message === 'string' && error.message && !error.isAxiosError) {
    return error.message;
  }

  return i18n.t('common:errors.generic');
};

/** One-way history (backend openDayPolicy): 422 for writes into the computed past. */
export const OPEN_DAY_ERROR_CODES = ['EVENT_BEFORE_OPEN_DAY', 'EVENT_PAST_LOCKED'] as const;

const errorBody = (error: any): any => error?.data ?? error?.response?.data ?? null;

/** True for EVENT_BEFORE_OPEN_DAY / EVENT_PAST_LOCKED (carried as `errorCode` or `code`). */
export const isOpenDayError = (error: any): boolean => {
  const body = errorBody(error);
  const code = body?.errorCode || body?.code;
  return typeof code === 'string' && (OPEN_DAY_ERROR_CODES as readonly string[]).includes(code);
};

/**
 * Text of an open-day rejection: the server message (localized) plus its
 * suggestion; without a server message — the bundled `simulation:openDay.*` text.
 */
const getOpenDayErrorMessage = (error: any): string | null => {
  if (!isOpenDayError(error)) return null;
  const body = errorBody(error);
  const code = body.errorCode || body.code;
  let text = typeof body.message === 'string' && body.message ? body.message : '';
  if (!text) {
    text = code === 'EVENT_PAST_LOCKED'
      ? String(i18n.t('simulation:openDay.pastLocked'))
      : String(i18n.t('simulation:openDay.beforeOpenDay', {
        date: body.details?.openDay ? formatDate(body.details.openDay, 'medium') : '',
      }));
  }
  const suggestion = typeof body.suggestion === 'string' ? body.suggestion.trim() : '';
  return suggestion && !text.includes(suggestion) ? `${text} ${suggestion}` : text;
};

/**
 * Извлекает код ошибки если он есть
 * Поддерживает RTK Query и Axios форматы
 */
export const getErrorCode = (error: any): string | null => {
  // RTK Query format
  if (error?.data?.errorCode) {
    return error.data.errorCode;
  }
  // Axios format
  if (error?.response?.data?.errorCode) {
    return error.response.data.errorCode;
  }
  return null;
};

/**
 * Извлекает дополнительные мета-данные ошибки
 * Поддерживает RTK Query и Axios форматы
 */
export const getErrorMeta = (error: any): any => {
  // RTK Query format
  if (error?.data?.meta) {
    return error.data.meta;
  }
  // Axios format
  if (error?.response?.data?.meta) {
    return error.response.data.meta;
  }
  return null;
};

export const toastError = (error: any): void => {
  if (isRequestHaltedError(error)) {
    return;
  }

  const message = getErrorMessage(error);

  if (messageApi) {
    messageApi.error(message);
  } else {
    antdMessage.error(message);
  }
};

export const toastSuccess = (text: string): void => {
  if (messageApi) {
    messageApi.success(text);
  } else {
    antdMessage.success(text);
  }
};

export const useToast = () => {
  const { message } = App.useApp();
  useEffect(() => {
    setMessageInstance(message);
  }, [message]);

  return {
    toastError: (err: any) => message.error(getErrorMessage(err)),
    toastSuccess: (text: string) => message.success(text),
  };
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  useToast();
  return <>{children}</>;
};
